import {
  breakpointSizes,
  defaultTheme,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  AccountAdditionalInfoPageFlatFields,
  amexCardTypes,
  amexIssuerCodes,
  RegisterAccountCountryFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import ReviewEnrollCard from 'src/app/ReviewEnrollCard/ReviewEnrollCard'
import AddressForm, {
  AddressFormFields,
} from 'src/component-form/AddressForm/AddressForm'
import EditCardDetailsModal from 'src/component-modal/EditCardDetailsModal/EditCardDetailsModal'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CardTypeTooltip from 'src/components/CardTypeTooltip/CardTypeTooltip'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RadioItems from 'src/components/RadioItems/RadioItems'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import TextDropdown, {
  TextOption,
} from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { Issuers, IssuerSchemaFilterFields, SupportedBrands } from 'src/enums'
import { useBackgroundDispatch, useScrollToScrollGroupRefs } from 'src/hooks'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { CountryListModel } from 'src/models/country.models'
import { IssuerIntegratorListModel } from 'src/models/issuerIntegrator.models'
import {
  accountOverviewUrl,
  PRICING_ROUTE,
  registerAccountUrl,
} from 'src/navigation/urls'
import { getCreditCardsActiveSourceCountStatistics } from 'src/redux/actions/data/statistics.actions'
import { selectAllStatisticsLoadedFromState } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectCreditCardsActiveSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import {
  selectAppName,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

const FormSectionDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
`

const FormSectionTitleSpan = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )};
`
interface RegisterAccountAdditionalInfoPageProps {
  selectedIssuer?: IssuerIntegratorListModel
  flatFields: AccountAdditionalInfoPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountAdditionalInfoPageFlatFields,
    value: AccountAdditionalInfoPageFlatFields[keyof AccountAdditionalInfoPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountAdditionalInfoPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  flatFieldsTopmostScrollGroup?: number
  industryOptions: TextOption[]
  issuerFields: Record<string, string>
  onIssuerFieldChange: (fieldName: string, value: string) => void
  issuerFieldsErrors: Partial<
    Record<string, Array<React.ReactElement | string>>
  >
  issuerFieldsTopmostScrollGroup?: number
  selectedCountry?: CountryListModel
  countryOptions: TextOption[]
  countryFields: RegisterAccountCountryFields
  onCountryFieldChange: (
    fieldName: keyof RegisterAccountCountryFields,
    value: RegisterAccountCountryFields[keyof RegisterAccountCountryFields],
  ) => void
  countryFieldsErrors: Partial<
    Record<
      keyof RegisterAccountCountryFields,
      Array<React.ReactElement | string>
    >
  >
  countryFieldsTopmostScrollGroup?: number
  onNavBack: () => void
  onSubmit: () => void
  hasOrganization: boolean
  onAddressFormBack?: () => void
  onReviewCreateCardDetails?: () => void
  on3dsVerificationStart?: () => void
  on3dsVerificationError?: () => void
}

const RegisterAccountAdditionalInfoPage: React.FC<
  RegisterAccountAdditionalInfoPageProps
> = ({
  selectedIssuer,
  flatFields,
  onFlatFieldChange,
  flatFieldsErrors,
  flatFieldsTopmostScrollGroup,
  industryOptions,
  issuerFields,
  onIssuerFieldChange,
  issuerFieldsErrors,
  issuerFieldsTopmostScrollGroup,
  selectedCountry,
  countryOptions,
  countryFields,
  onCountryFieldChange,
  countryFieldsErrors,
  countryFieldsTopmostScrollGroup,
  onNavBack,
  onSubmit,
  onAddressFormBack,
  onReviewCreateCardDetails,
  on3dsVerificationStart,
  on3dsVerificationError,
  hasOrganization,
}): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveSourceCount = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )

  const appName = useSelector(selectAppName)
  const brand = useSelector(selectBrandName)
  const isJpm = brand === SupportedBrands.Jpm
  const isExtendBrand = brand === SupportedBrands.Extend
  const {
    enable_partner_bank_astrada_onboarding: enablePartnerBankAstradaOnboarding,
    enable_embedded_chargebee_checkout: enableEmbeddedChargebeeCheckout,
  } = useSelector(selectFeatureFlaggerFromState) || {}

  const { analytics } = useAnalyticsContext()

  const selectedCardType = amexCardTypes.filter(
    (cardType) => cardType.value === flatFields.cardType,
  )[0]
  const selectedCardTypeIndex = amexCardTypes.indexOf(selectedCardType)

  const [selectedIndex, setSelectedIndex] = useState(
    selectedCardTypeIndex > -1 ? selectedCardTypeIndex : -1,
  )

  const [showReviewModal, setShowReviewModal] = useState<boolean>(false)
  const [showEditCardDetails, setShowEditCardDetails] = useState<boolean>(false)

  const dispatch = useBackgroundDispatch()

  const translate = useTranslate()

  useEffect((): void => {
    dispatch(getCreditCardsActiveSourceCountStatistics())
  }, [])

  const scrollRef0 = useRef<HTMLDivElement>(null)
  const scrollRef1 = useRef<HTMLDivElement>(null)
  const scrollRef2 = useRef<HTMLDivElement>(null)
  useScrollToScrollGroupRefs(
    [scrollRef0, scrollRef1, scrollRef2],
    [
      flatFieldsTopmostScrollGroup,
      issuerFieldsTopmostScrollGroup,
      countryFieldsTopmostScrollGroup,
    ],
  )

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onSubmit()
  }

  const astrada = useAstrada()
  const history = useHistory()

  useEffect(() => {
    onFlatFieldChange('country', selectedCountry?.iso)
  }, [selectedCountry])

  const filteredIssuerSchemaFields =
    selectedIssuer?.schema.filter(
      (field) =>
        field.name !== IssuerSchemaFilterFields.cardNumber &&
        field.name !== IssuerSchemaFilterFields.expires &&
        field.name !== IssuerSchemaFilterFields.cvc,
    ) || []

  const handleCardTypeSelection = (val: string): void => {
    analytics?.track('register account - billing info - card type', {
      card_type: val,
      screen: registerAccountUrl(),
    })
    onFlatFieldChange('cardType', val)
  }

  const radioItem = (
    key: string | null,
    fieldText: string | React.ReactElement,
  ): React.ReactElement => {
    return (
      <span key={key}>
        <p
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {fieldText}
        </p>
      </span>
    )
  }

  // Astrada-eligible BINs on Extend take the v2 path, rolled out per-org via
  // the PHYSICAL_CARD_TRANSACTIONS_FEED user feature.
  const enableAstradaEnrollment = isExtendBrand
  const isAmexIssuer = amexIssuerCodes.includes(selectedIssuer?.code as Issuers)

  return (!statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded &&
    !isJpm) ||
    (!enableAstradaEnrollment && !selectedIssuer) ? (
    <LoadingBackgroundPage />
  ) : (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 60px;
      `}
    >
      {/*
        REVIEW MODAL CONTENT
        if this piece of state is true, do not render any of the other remaining code.
        Just render this create card review content
      */}
      {showReviewModal &&
      astrada.addressData &&
      astrada.binData &&
      astrada.cardData ? (
        <>
          <ReviewEnrollCard
            submissionData={{
              addressData: astrada.addressData,
              binData: astrada.binData,
              cardData: astrada.cardData,
              issuerData: astrada.issuerData,
            }}
            goBackText={translate('Back to Additional Info')}
            onBack={() => {
              setShowReviewModal(false)
            }}
            onVerifyError={on3dsVerificationError}
            onVerifyStart={on3dsVerificationStart}
            onVerifyComplete={(cardId) => {
              if (astrada.onboarding) {
                // Redirect to pricing page
                history.push(PRICING_ROUTE(cardId))
              } else {
                // Redirect to RCN detail page
                history.push(accountOverviewUrl(cardId))
              }
              astrada.reset()
            }}
            onUpdateCard={() => setShowEditCardDetails(true)}
          />
          {showEditCardDetails && (
            <EditCardDetailsModal
              isOpen
              onClose={() => setShowEditCardDetails(false)}
              submissionData={{
                addressData: astrada.addressData,
                binData: astrada.binData,
                cardData: astrada.cardData,
                issuerData: astrada.issuerData,
              }}
              onSubmit={(updated) => {
                astrada.updateState({
                  cardData: updated.cardData,
                  addressData: updated.addressData,
                  issuerData: updated.issuerData,
                })
                setShowEditCardDetails(false)
              }}
            />
          )}
        </>
      ) : (
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              max-width: 440px;
            `}
          >
            {translate("Great! Now let's fill out some additional information")}
          </div>

          <div
            css={css`
              margin-top: 30px;
              margin-bottom: 40px;
              max-width: 500px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                max-width: none;
              `)}
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              isJpm
                ? 'Your account requires a little more info to connect to ${appName}. Please fill that out below.'
                : 'Your credit card requires a little more info to connect to ${appName}. Please fill that out below.',
              { appName },
            )}
          </div>
          {/* FEATURE FLAG (ASTRADA) SPLIT HERE */}
          {enableAstradaEnrollment ? (
            <AddressForm
              issuerFields={selectedIssuer?.schema}
              initialValues={astrada.addressData}
              onSubmit={(values: AddressFormFields, issuerFields) => {
                // Store the values in a context provider
                // Then navigate to another page
                astrada.updateState({
                  cardData: astrada.cardData
                    ? { ...astrada.cardData }
                    : undefined,
                  addressData: {
                    ...values,
                  },
                  issuerData: { ...issuerFields },
                })

                onReviewCreateCardDetails?.()
                // Reordered onboarding (EX-39123): skip the acknowledgements
                // (ReviewEnrollCard) step and route to pricing first. The
                // ack modal is reopened after Chargebee checkout success —
                // which only fires in embedded mode. In redirect mode the
                // user leaves the app and the callback never runs, so fall
                // back to the original acks-before-pricing order.
                if (
                  enablePartnerBankAstradaOnboarding &&
                  enableEmbeddedChargebeeCheckout &&
                  astrada.onboarding
                ) {
                  history.push(PRICING_ROUTE())
                } else {
                  setShowReviewModal(true)
                }
              }}
              previousElement={
                <div
                  // Inline css here because overriding class on legacy pages is tricky
                  css={css`
                    font-size: 14px;
                  `}
                  className="font-linktext flex-row gap-2 align-center justify-center"
                  role="button"
                  onClick={() => {
                    onAddressFormBack?.()
                    onNavBack()
                  }}
                  tabIndex={-1}
                  // For WAI-ARIA considerations
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onAddressFormBack?.()
                      onNavBack()
                    }
                  }}
                >
                  <SvgChevronLeft />
                  {translate('Back to Card Info')}
                </div>
              }
            />
          ) : (
            /* EXISTING LOGIC HERE */
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  margin-top: 30px;
                  align-items: center;
                  border: 1px solid
                    ${isJpm
                      ? themeConstants.colors.highlight
                      : defaultTheme.colors.cta_dark};
                  border-radius: 10px;
                `}
              >
                {selectedIssuer?.url === undefined ? (
                  <span
                    id={selectedIssuer?.name + ' Img'}
                    css={css`
                      width: 140px;
                      padding-top: 20px;
                      padding-bottom: 20px;
                      text-align: center;
                      justify-content: center;
                      display: flex;
                      ${getFontCss(
                        32,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {selectedIssuer?.name}
                  </span>
                ) : (
                  <img
                    id={selectedIssuer.name + ' Img'}
                    css={css`
                      width: 140px;
                      padding-top: 20px;
                      padding-bottom: 20px;
                    `}
                    src={selectedIssuer.url}
                    alt={selectedIssuer.name}
                  />
                )}
              </div>
              {!_.isEmpty(selectedIssuer?.helpText) && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    margin-top: 20px;
                    border: 1px solid ${styleConstants.colors.yellow};
                    background-color: ${styleConstants.colors.gray_lightest};
                    border-radius: 10px;
                    padding: 20px;
                  `}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {selectedIssuer.helpText.message}
                  </span>
                  {selectedIssuer.helpText.linkUrl && (
                    <HoverableLink
                      css={css`
                        margin-top: 12px;
                        width: fit-content;
                      `}
                      underline
                      size={HoverableLinkSizes.Small}
                      href={selectedIssuer.helpText.linkUrl}
                      ariaLabelSource={
                        selectedIssuer.helpText.linkLabel ||
                        selectedIssuer.helpText.linkUrl
                      }
                      onClick={(): void => {
                        analytics?.track(
                          'register account - billing info - help center',
                          {
                            intent:
                              'view help center for more info on amex RCN reg',
                            interaction: 'text link press',
                            screen: registerAccountUrl(),
                          },
                        )
                      }}
                    >
                      {selectedIssuer.helpText.linkLabel ||
                        selectedIssuer.helpText.linkUrl}
                    </HoverableLink>
                  )}
                </div>
              )}
              <form
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
                onSubmit={handleOnSubmit}
              >
                <>
                  <FormSectionDiv ref={scrollRef0}>
                    {isAmexIssuer && (
                      <>
                        <FormSectionTitleSpan>
                          <div
                            css={css`
                              display: flex;
                            `}
                          >
                            <span>
                              <Trns source={'Card Type'} />
                            </span>
                            <span>
                              <CardTypeTooltip
                                openLeft={false}
                                onHover={(): void => {
                                  analytics?.track(
                                    'register account - billing info - card type tooltip hover',
                                    {
                                      screen: registerAccountUrl(),
                                    },
                                  )
                                }}
                              />
                            </span>
                          </div>
                        </FormSectionTitleSpan>
                        <div
                          css={css`
                            margin-top: 8px;
                            ${getFontCss(
                              12,
                              styleConstants.colors.gray_darkest,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          <Trns
                            source={
                              'Telling us your card type will help us speed up this registration'
                            }
                          />
                        </div>
                        <RadioItems
                          css={css`
                            width: 100%;
                            margin: 20px 0px 30px 0px;
                          `}
                          items={amexCardTypes.map((cardType) =>
                            radioItem(cardType.value, cardType.fieldText),
                          )}
                          currentIndex={selectedIndex}
                          setKey={(val): void => handleCardTypeSelection(val)}
                          setIndex={setSelectedIndex}
                          horizontalAlign
                          staticOnHover
                          noBorder
                          alignStart
                          gap={'40px'}
                          errors={flatFieldsErrors['cardType']}
                          ariaLabels={amexCardTypes.map((ct) =>
                            typeof ct.fieldText === 'string'
                              ? translate(ct.fieldText)
                              : translate(ct.fieldText.props.source),
                          )}
                        />
                      </>
                    )}
                    <FormSectionTitleSpan>
                      <Trns source={'Billing Address'} />
                    </FormSectionTitleSpan>
                    <TextDropdown
                      data-testid={'country'}
                      id={'country'}
                      css={css`
                        margin-top: 20px;
                      `}
                      errors={flatFieldsErrors['country']}
                      placeholder={translateSingleString(
                        locale,
                        'Select ${fieldLabel}',
                        {
                          fieldLabel: translateSingleString(locale, 'Country'),
                        },
                      )}
                      fieldLabel={translateSingleString(locale, 'Country')}
                      isMulti={false}
                      selectedValues={
                        countryOptions.length === 1
                          ? new Set([`${countryOptions[0]?.value}`])
                          : flatFields['country'] !== undefined
                            ? new Set([flatFields['country']])
                            : new Set([])
                      }
                      chooseOption={(val: string): void => {
                        onFlatFieldChange('country', val)
                        onCountryFieldChange('province', undefined)
                      }}
                      optionGroups={[
                        {
                          options: countryOptions,
                        },
                      ]}
                      disabled={!(countryOptions.length > 1)}
                    />
                    {selectedCountry !== undefined &&
                      selectedCountry.fields.map((field, i) =>
                        field.options !== undefined ? (
                          <TextDropdown
                            id={field.label}
                            data-testid={'state'}
                            key={`${flatFields.country}-${field.name}-${i}`}
                            css={css`
                              margin-top: 20px;
                            `}
                            errors={countryFieldsErrors[field.name]}
                            placeholder={translateSingleString(
                              locale,
                              'Select ${fieldLabel}',
                              { fieldLabel: field.label },
                            )}
                            fieldLabel={field.label}
                            isMulti={false}
                            selectedValues={
                              typeof countryFields[field.name] === 'string'
                                ? new Set([countryFields[field.name] || ''])
                                : new Set([])
                            }
                            chooseOption={(val: string): void => {
                              onCountryFieldChange(field.name, val)
                            }}
                            optionGroups={[
                              {
                                options: field.options.map((opt) => ({
                                  value: opt.code,
                                  details: {
                                    text: opt.label,
                                  },
                                })),
                              },
                            ]}
                          />
                        ) : (
                          <TextInputField
                            id={field.name}
                            key={`${flatFields.country}-${field.name}-${i}`}
                            css={css`
                              margin-top: 20px;
                            `}
                            errors={countryFieldsErrors[field.name]}
                            design={TextInputFieldDesigns.New}
                            fieldLabel={field.label}
                            value={
                              field.name === 'postal'
                                ? countryFields[field.name]?.toUpperCase()
                                : countryFields[field.name]
                            }
                            onChange={(val: string): void => {
                              onCountryFieldChange(field.name, val)
                            }}
                            placeholder={translateSingleString(
                              locale,
                              'Enter ${fieldLabel}',
                              { fieldLabel: field.label },
                            )}
                          />
                        ),
                      )}
                  </FormSectionDiv>
                  {/* need backend for this */}
                  {selectedIssuer &&
                    filteredIssuerSchemaFields.length > 0 &&
                    brand !== SupportedBrands.Jpm && (
                      <FormSectionDiv ref={scrollRef1}>
                        <FormSectionTitleSpan id={'cardHeader'}>
                          <Trns source={'Additional Info'} />
                        </FormSectionTitleSpan>
                        {filteredIssuerSchemaFields.map((field, i) => (
                          <div
                            key={i}
                            css={css`
                              display: flex;
                              flex-direction: column;
                              margin-top: 20px;
                            `}
                          >
                            <TextInputField
                              id={field.label}
                              errors={issuerFieldsErrors[field.name]}
                              design={TextInputFieldDesigns.New}
                              fieldLabel={field.label}
                              value={issuerFields[field.name]}
                              onChange={(val: string): void => {
                                onIssuerFieldChange(
                                  field.name,
                                  field.formattingFunction(val),
                                )
                              }}
                              placeholder={translateSingleString(
                                locale,
                                'Enter ${fieldLabel}',
                                {
                                  fieldLabel: field.label,
                                },
                              )}
                              inputMode={field.inputMode}
                            />
                            {field.description !== undefined && (
                              <span
                                css={css`
                                  ${getFontCss(
                                    12,
                                    styleConstants.colors.gray_darkest,
                                    styleConstants.fontFamilies.regular,
                                  )};
                                  padding: 5px 20px;
                                `}
                                dangerouslySetInnerHTML={{
                                  __html: field.description,
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </FormSectionDiv>
                    )}
                  {/* display company info section only if user is not part of an org or has not registered any RCNs */}
                  {!(hasOrganization || creditCardsActiveSourceCount > 0) && (
                    <FormSectionDiv ref={scrollRef2}>
                      <FormSectionTitleSpan id={'compInfo'}>
                        <Trns source={'Company Information'} />
                      </FormSectionTitleSpan>
                      <TextInputField
                        id={'compName'}
                        css={css`
                          margin-top: 20px;
                        `}
                        errors={flatFieldsErrors['companyName']}
                        design={TextInputFieldDesigns.New}
                        fieldLabel={<Trns source={'Company Name'} />}
                        value={flatFields['companyName']}
                        onChange={(val: string): void => {
                          onFlatFieldChange('companyName', val)
                        }}
                        placeholder={translateSingleString(
                          locale,
                          'Enter Company Name',
                        )}
                      />
                      <TextDropdown
                        id={'industry'}
                        css={css`
                          margin-top: 20px;
                        `}
                        fieldLabel={<Trns source={'Industry'} />}
                        optionGroups={[
                          {
                            label: <Trns source={'Existing Options'} />,
                            options: industryOptions,
                          },
                        ]}
                        isMulti={false}
                        selectedValues={
                          flatFields['industry'] !== undefined
                            ? new Set([flatFields['industry']])
                            : new Set<string>()
                        }
                        chooseOption={(val: string): void => {
                          onFlatFieldChange('industry', val)
                        }}
                        placeholder={translateSingleString(
                          locale,
                          'Select Industry',
                        )}
                        errors={flatFieldsErrors['industry']}
                        validateNewOption={(val: string): boolean =>
                          val.length > 0
                        }
                        newOptionLabel={<Trns source={'New Label'} />}
                        isCreatable
                      />
                    </FormSectionDiv>
                  )}
                </>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 40px;
                  `}
                >
                  <HoverableLink
                    css={css`
                      display: flex;
                      align-items: center;
                    `}
                    onClick={onNavBack}
                    ariaLabelSource={
                      isJpm ? 'Back to Account Info' : 'Back to Card Info'
                    }
                  >
                    <SvgChevronLeft />
                    <Trns
                      source={
                        isJpm ? 'Back to Account Info' : 'Back to Card Info'
                      }
                    />
                  </HoverableLink>
                  <Button
                    testId="next-button"
                    id={'accountNameButton'}
                    css={css`
                      align-self: flex-end;
                    `}
                    type={ButtonTypes.submit}
                    design={ButtonDesigns.designCta}
                    text={<Trns source={'Continue to Account Name'} />}
                    big
                  />
                </div>
              </form>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default RegisterAccountAdditionalInfoPage
